import React, { FC, useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress } from '@mui/material';
import UmbarcoApi from 'src/api/UmbarcoApi';
import useVendorBooth from 'src/hooks/useVendorBooth';
import CloseIcon from '@mui/icons-material/Close';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ConfirmationDialog from './controls/ConfirmationDialog';

const BASE_IMAGE_URL = process.env.REACT_APP_BASEIMAGE_URL;

interface MediaUploadProps {
  id: string; //matches the alias of the boothProperty
  title?: string; //Text shown above the component
  hints?: string[]; //Show hints under the upload button as small italic text
  showRequiredText?: boolean; //Show an asterisk next to the title
}

const MediaUpload: FC<React.PropsWithChildren<MediaUploadProps>> = ({ id, title, hints, showRequiredText }) => {
  const { vendorBooth, deleteMedia, updateProperty } = useVendorBooth();
  const [mediaLoading, setMediaLoading] = useState<boolean>(false);
  const [mediaUrl, setMediaUrl] = useState<string>('');
  const [mediaFileName, setMediaFileName] = useState<string>('');
  const [errors, setErrors] = useState<string[]>([]);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const udi = vendorBooth?.boothProperties?.find((x) => x.alias === id)?.value?.toString() ?? '';

  useEffect(() => {
    if (udi) {
      UmbarcoApi.getImageUrlFromUdi(udi)
        .then((r) => {
          if (r.success === false) {
            setErrors(r.errors);
            return;
          }

          let path = r.response;

          setMediaUrl(BASE_IMAGE_URL + path);
          setMediaFileName(path.match(/[^/]+$/)[0]);
        })
        .catch((err) => {
          console.error('Error getting image url from udi', err);
          setErrors(['Error getting image url from udi ' + err]);
        });
    }
  }, [udi, vendorBooth.id]);

  const onFileUploaded = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log('File uploaded', e.target.files);
    if (mediaUrl != '' && !confirm('Adding this file will overwrite the file already there. Are you sure you want to continue?')) {
      //Reset the file input if the user cancels the operation
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    if (e.target.files && e.target.files.length > 0) {
      setErrors([]);
      const file = e.target.files[0];

      if (!file) {
        setErrors(['No file selected']);
        return;
      }

      //find megabytes of file
      const fileSizeMB = file.size / 1024 / 1024;

      if (file.name.endsWith('pdf') && fileSizeMB > 200) {
        setErrors(['PDF file size cannot exceed 200MB']);
        return;
      }

      if (!file.name.endsWith('pdf') && fileSizeMB > 3) {
        setErrors(['Image file size cannot exceed 3MB']);
        return;
      }

      setMediaLoading(true);
      UmbarcoApi.uploadFile(file, vendorBooth.id, e.target.name)
        .then((res) => {
          if (res.success === true) {
            console.log('File uploaded successfully', res.response);
            //set udi in boothProperties, let the useEffect that is listening on udi handle the loading of the image
            updateProperty(id, res.response);
          } else {
            setErrors(res.errors);
          }
        })
        .finally(() => {
          setMediaLoading(false);
        });
    }
  };

  const openDeleteConfirmation = () => {
    setDeleteConfirmationOpen(true);
  };

  const deleteConfirmation = () => {
    setDeleteConfirmationOpen(false);

    setErrors([]);
    setMediaUrl('');
    setMediaFileName('');
    deleteMedia(udi, id, vendorBooth.id);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {title && (
        <span className="image-upload__content">
          {title} {showRequiredText && <span style={{ color: 'red' }}>* REQUIRED</span>}
        </span>
      )}
      <div style={{ display: 'flex', flexDirection: 'row' }}>
        <Button variant="contained" component="label" className="image-upload">
          <input hidden name={id} type="file" ref={fileInputRef} onChange={onFileUploaded} />
        </Button>
        {mediaLoading ? (
          <CircularProgress />
        ) : (
          mediaUrl != '' && (
            <div className={`booth-media-content ${mediaUrl.endsWith('.pdf') ? 'is-pdf' : ''}`}>
              {mediaUrl.endsWith('.pdf') ? (
                <>
                  <PictureAsPdfIcon />
                  <Button className="booth-media__delete" onClick={openDeleteConfirmation}>
                    <CloseIcon />
                  </Button>
                </>
              ) : (
                <div className="booth-image-container">
                  <img
                    title={mediaUrl}
                    className="booth-image d-block"
                    src={mediaUrl}
                    onError={() => {
                      setMediaUrl('https://www.bfgsupply.com/content/img/img_not_avail.png');
                    }}
                  />
                  <Button className="booth-media__delete" onClick={openDeleteConfirmation}>
                    <CloseIcon />
                  </Button>
                </div>
              )}

              <a href={mediaUrl} className="booth-media__file-name" target="_blank">
                {mediaFileName}
              </a>
            </div>
          )
        )}
      </div>
      <Box sx={{ pt: 1 }}>
        {hints?.map((hint, index) => (
          <Box sx={{ fontSize: 'small', color: '#777' }} key={index}>
            <i>* {hint}</i>
          </Box>
        ))}
      </Box>
      {errors && errors.length > 0 && (
        <span style={{ color: 'red' }}>
          <b>{errors.join(' ')}</b>
        </span>
      )}

      <ConfirmationDialog
        title="Delete Item"
        open={deleteConfirmationOpen}
        children={
          <p>
            Are you sure you want to delete <strong>{mediaFileName}</strong>?
          </p>
        }
        onClose={(confirmed) => {
          setDeleteConfirmationOpen(false);
          if (!confirmed) return;
          deleteConfirmation();
        }}
      />
    </Box>
  );
};

export default MediaUpload;
